import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PRE_ASSESSMENT, RATING } from '@/constants/testIds';
import Navigation from '@/components/Navigation';
import RatingModal from '@/components/RatingModal';
import { validateUserFiles, USER_UPLOAD_ACCEPT_ATTR, MAX_FILE_SIZE_LABEL } from '@/lib/fileValidation';
import { bookingAgeStatus, AGE_STATUS_LABEL, AGE_STATUS_BADGE } from '@/lib/bookingStatus';
import { CheckCircle, Clock, AlertTriangle, FileText, Calendar as CalendarIcon, X, DollarSign, Download, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Returns 6 weeks × 7 days grid of { dateStr, day, inMonth } items.
// Week starts on Monday to match AU business convention.
function buildMonthGrid(year, monthIndex) {
  const firstOfMonth = new Date(year, monthIndex, 1);
  const firstWeekday = (firstOfMonth.getDay() + 6) % 7; // 0=Mon..6=Sun
  const start = new Date(year, monthIndex, 1 - firstWeekday);
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    cells.push({
      dateStr: `${y}-${m}-${day}`,
      day: d.getDate(),
      weekday: d.getDay(), // 0=Sun..6=Sat
      inMonth: d.getMonth() === monthIndex
    });
  }
  return cells;
}

function todayLocalISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const URGENCY_LEVELS = [
  { value: 'low', label: 'Low — within 4 weeks' },
  { value: 'medium', label: 'Medium — within 2 weeks' },
  { value: 'high', label: 'High — within 1 week' },
  { value: 'urgent', label: 'Urgent — within 48 hours' }
];

const STAGE_LABELS = {
  intake_submitted: 'Intake Submitted',
  deposit_paid: 'Deposit Paid',
  report_issued: 'Report Issued',
  next_stage_confirmed: 'Next Stage Confirmed',
  next_stage_booked: 'Next Stage Booked',
  next_stage_paid: 'Next Stage Paid',
  completed: 'Workflow Complete'
};

// All booking times are displayed in AEST (Australia/Brisbane — UTC+10 fixed, no DST)
// so the label is identical for any viewer regardless of their browser timezone.
const AEST_TZ = 'Australia/Brisbane';

function formatSlot(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: AEST_TZ,
    timeZoneName: 'short'
  });
}

function groupSlotsByDate(slots) {
  const grouped = {};
  slots.forEach((s) => {
    const date = s.slot.split('T')[0];
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(s);
  });
  return grouped;
}

export default function Bookings() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processingPayment, setProcessingPayment] = useState({});
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [holidays, setHolidays] = useState(new Set());
  // 5-business-day lead-time window — the All Build Consult team's guaranteed
  // initial review & response window. Both pieces come from
  // /api/availability/holidays and are used by the calendar to grey out the
  // blocked-out dates immediately on render.
  const [earliestBookableDate, setEarliestBookableDate] = useState(null);
  const [leadBusinessDays, setLeadBusinessDays] = useState(5);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const [form, setForm] = useState({
    service_id: '',
    enquiry_category: '',
    description: '',
    expected_outcome: '',
    urgency: 'medium',
    property_address: '',
    budget: 0
  });

  const selectedService = useMemo(
    () => servicesCatalog.find((s) => s.id === form.service_id) || null,
    [servicesCatalog, form.service_id]
  );

  const servicesByCategory = useMemo(() => {
    const grouped = {};
    servicesCatalog.forEach((s) => {
      if (!grouped[s.category]) grouped[s.category] = [];
      grouped[s.category].push(s);
    });
    return grouped;
  }, [servicesCatalog]);

  // Categorise bookings by age/status (new | current | old) for the tabs
  const bookingsByStatus = useMemo(() => {
    const buckets = { new: [], current: [], old: [] };
    bookings.forEach((b) => {
      const s = bookingAgeStatus(b);
      if (buckets[s]) buckets[s].push(b);
    });
    return buckets;
  }, [bookings]);

  const filteredBookings = useMemo(() => {
    if (activeTab === 'all') return bookings;
    return bookingsByStatus[activeTab] || [];
  }, [bookings, bookingsByStatus, activeTab]);

  // Rating
  const [ratingBooking, setRatingBooking] = useState(null);
  const [ratedBookingIds, setRatedBookingIds] = useState(new Set());
  const [consultants, setConsultants] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      // Auth cookie is sent automatically (axios.defaults.withCredentials=true).
      // Only include Authorization header when an in-memory token is present.
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      // allSettled so a single 401 doesn't tank the holidays/availability fetch
      // — the public endpoints (services-catalog, availability, holidays,
      // consultants) must still populate so the calendar can render even when
      // the user-scoped /bookings call fails for a moment during auth boot.
      const [bookingsRes, slotsRes, consultantsRes, catalogRes, holidaysRes] = await Promise.allSettled([
        axios.get(`${API}/bookings`, { headers: authHeaders }),
        axios.get(`${API}/availability`),
        axios.get(`${API}/consultants`),
        axios.get(`${API}/services-catalog`),
        axios.get(`${API}/availability/holidays`)
      ]);
      if (bookingsRes.status === 'fulfilled') setBookings(bookingsRes.value.data);
      if (slotsRes.status === 'fulfilled') setSlots(slotsRes.value.data);
      if (consultantsRes.status === 'fulfilled') setConsultants(consultantsRes.value.data);
      if (holidaysRes.status === 'fulfilled') {
        setHolidays(new Set(holidaysRes.value.data.holidays || []));
        setEarliestBookableDate(holidaysRes.value.data.earliest_bookable_date || null);
        setLeadBusinessDays(holidaysRes.value.data.lead_business_days || 5);
      }

      // Filter out workflow-gated services AND coming-soon Trade Works from the booking dropdown's default
      const bookable = catalogRes.status === 'fulfilled'
        ? catalogRes.value.data.filter((s) => !s.requires_workflow_complete)
        : [];
      setServicesCatalog(bookable);

      // Default-select the first NON-Trade-Works service so the form is valid out of the box
      setForm((prev) => {
        if (prev.service_id) return prev;
        const first = bookable.find((s) => s.category !== 'Trade Works & Administration');
        return first
          ? {
              ...prev,
              service_id: first.id,
              enquiry_category: `${first.category} — ${first.name}`,
              budget: first.deposit_amount
            }
          : prev;
      });

      if (consultantsRes.status === 'fulfilled') {
        const allRatings = await Promise.all(
          consultantsRes.value.data.map((c) => axios.get(`${API}/ratings/consultant/${c.id}`).catch(() => ({ data: [] })))
        );
        const ratedIds = new Set();
        allRatings.forEach((r) => r.data.forEach((rt) => ratedIds.add(rt.booking_id)));
        setRatedBookingIds(ratedIds);
      }

      // Default selected date to first available day; move calendar to that month
      if (slotsRes.status === 'fulfilled') {
        const firstAvail = slotsRes.value.data.find((s) => s.available);
        if (firstAvail && !selectedDate) {
          const d = firstAvail.slot.split('T')[0];
          setSelectedDate(d);
          const [y, m] = d.split('-').map(Number);
          setCalendarMonth({ year: y, month: m - 1 });
        }
      }
    } catch (err) {
      console.error('Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedDate]);

  useEffect(() => {
    // Wait for the App-level user bootstrap to settle. Without this, we'd
    // race the cookie-based /auth/me call and 401 our way through fetchData,
    // wiping out the public-endpoint data (holidays, services, slots) too.
    if (!user) return;
    fetchData();
  }, [user, fetchData]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const { valid, errors } = validateUserFiles(files);
    if (errors.length > 0) {
      setError(errors.join(' '));
    } else {
      setError('');
    }
    setPendingFiles((prev) => [...prev, ...valid]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedSlot) {
      setError('Please select an available time slot');
      return;
    }
    setSubmitting(true);
    try {
      const bookingRes = await axios.post(
        `${API}/bookings`,
        { ...form, preferred_slot: selectedSlot },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newBookingId = bookingRes.data.id;

      // Upload photos
      for (const file of pendingFiles) {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(
          `${API}/bookings/${newBookingId}/photos`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      setShowForm(false);
      setPendingFiles([]);
      setSelectedSlot('');
      const firstService = servicesCatalog[0];
      setForm({
        service_id: firstService?.id || '',
        enquiry_category: firstService ? `${firstService.category} — ${firstService.name}` : '',
        description: '',
        expected_outcome: '',
        urgency: 'medium',
        property_address: '',
        budget: firstService?.deposit_amount || 0
      });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async (bookingId) => {
    setProcessingPayment({ ...processingPayment, [bookingId]: true });
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/checkout?booking_id=${bookingId}&origin_url=${encodeURIComponent(originUrl)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Payment failed:', err);
      setProcessingPayment({ ...processingPayment, [bookingId]: false });
    }
  };

  const handleRatingSubmit = async ({ stars, comment, booking_id, consultant_email }) => {
    const c = consultants.find((cc) => cc.email === consultant_email);
    if (!c) throw new Error('Consultant not found');
    await axios.post(
      `${API}/ratings`,
      { consultant_id: c.id, booking_id, stars, comment },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setRatingBooking(null);
    fetchData();
  };

  const groupedSlots = useMemo(() => groupSlotsByDate(slots), [slots]);
  const slotsForSelectedDate = selectedDate ? (groupedSlots[selectedDate] || []) : [];

  // Calendar bounds: tomorrow → 90 days from today
  const todayIso = todayLocalISO();
  const minDateIso = useMemo(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, '0');
    const d = String(t.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);
  const maxDateIso = useMemo(() => {
    const t = new Date();
    t.setDate(t.getDate() + 90);
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, '0');
    const d = String(t.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  const monthGrid = useMemo(
    () => buildMonthGrid(calendarMonth.year, calendarMonth.month),
    [calendarMonth]
  );

  const monthLabel = useMemo(
    () => new Date(calendarMonth.year, calendarMonth.month, 1).toLocaleDateString(undefined, {
      month: 'long', year: 'numeric'
    }),
    [calendarMonth]
  );

  // Prev/Next bounds — allow navigation within [current month .. month containing maxDate]
  const now = new Date();
  const minMonth = now.getFullYear() * 12 + now.getMonth();
  const [maxY, maxM] = maxDateIso.split('-').map(Number);
  const maxMonth = maxY * 12 + (maxM - 1);
  const currentMonthIdx = calendarMonth.year * 12 + calendarMonth.month;
  const canPrev = currentMonthIdx > minMonth;
  const canNext = currentMonthIdx < maxMonth;

  const goPrevMonth = () => {
    if (!canPrev) return;
    const d = new Date(calendarMonth.year, calendarMonth.month - 1, 1);
    setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
  };
  const goNextMonth = () => {
    if (!canNext) return;
    const d = new Date(calendarMonth.year, calendarMonth.month + 1, 1);
    setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
  };

  const dayState = (cell) => {
    // Returns { selectable, reason } — reason for tooltip when disabled
    const isWeekend = cell.weekday === 0 || cell.weekday === 6;
    const isPastOrToday = cell.dateStr <= todayIso;
    const isOutsideWindow = cell.dateStr < minDateIso || cell.dateStr > maxDateIso;
    const isHoliday = holidays.has(cell.dateStr);
    const isInsideLeadTime = earliestBookableDate && cell.dateStr < earliestBookableDate;
    const dateSlots = groupedSlots[cell.dateStr] || [];
    const hasAnyAvailable = dateSlots.some((s) => s.available);
    if (isPastOrToday) return { selectable: false, reason: 'Past' };
    if (isWeekend) return { selectable: false, reason: 'Weekend' };
    if (isHoliday) return { selectable: false, reason: 'Public holiday' };
    if (isInsideLeadTime) {
      return {
        selectable: false,
        reason: `Reserved for All Build Consult's ${leadBusinessDays}-business-day review window — first available date is ${earliestBookableDate}`,
      };
    }
    if (isOutsideWindow) return { selectable: false, reason: 'Outside booking window' };
    if (!hasAnyAvailable) return { selectable: false, reason: 'Fully booked' };
    return { selectable: true, reason: '' };
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-6 py-8 mt-16">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-black mb-2" style={{ fontFamily: 'Outfit' }}>
              Bookings
            </h1>
            <p className="text-slate-600">
              All initial assessments are conducted by the <strong>All Build Consult team</strong>
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all font-medium"
            >
              + New Pre-Assessment
            </button>
          )}
        </div>

        {showForm && (
          <div className="grid-border bg-white p-8 mb-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit' }}>
                  Pre-Assessment Intake
                </h2>
                <p className="text-sm text-slate-600">
                  Your intake follows the All Build Consult <strong className="text-black">A · B · C</strong> —
                  documents, concerns, conclusion.
                </p>
              </div>
              <button onClick={() => setShowForm(false)} className="text-slate-500 hover:text-black">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* ABC quick-strip explainer */}
            <div
              data-testid="intake-abc-strip"
              className="mb-6 p-4 bg-gradient-to-r from-blue-50 via-white to-emerald-50 border-l-2 border-blue-600 rounded-sm grid grid-cols-1 md:grid-cols-3 gap-3"
            >
              <div className="text-xs text-slate-700">
                <span className="inline-flex items-center gap-1.5 font-bold text-blue-700 mb-0.5">
                  <span className="w-5 h-5 bg-blue-600 text-white rounded-full text-[10px] inline-flex items-center justify-center font-black">A</span>
                  All documents
                </span>
                <p className="text-slate-600">Upload photos, plans, scope &amp; quotes.</p>
              </div>
              <div className="text-xs text-slate-700">
                <span className="inline-flex items-center gap-1.5 font-bold text-blue-700 mb-0.5">
                  <span className="w-5 h-5 bg-blue-600 text-white rounded-full text-[10px] inline-flex items-center justify-center font-black">B</span>
                  Building concerns
                </span>
                <p className="text-slate-600">Tell us what worries you and what help you need.</p>
              </div>
              <div className="text-xs text-slate-700">
                <span className="inline-flex items-center gap-1.5 font-bold text-blue-700 mb-0.5">
                  <span className="w-5 h-5 bg-blue-600 text-white rounded-full text-[10px] inline-flex items-center justify-center font-black">C</span>
                  Conclusion &amp; result
                </span>
                <p className="text-slate-600">The outcome you'd like reported back.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Select a Service (Schedule of Reporting Services)
                </label>
                <select
                  data-testid={PRE_ASSESSMENT.categorySelect}
                  value={form.service_id}
                  onChange={(e) => {
                    const id = e.target.value;
                    const svc = servicesCatalog.find((s) => s.id === id);
                    setForm({
                      ...form,
                      service_id: id,
                      enquiry_category: svc ? `${svc.category} — ${svc.name}` : '',
                      budget: svc ? svc.deposit_amount : 0
                    });
                  }}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  required
                >
                  <option value="" disabled>— Choose a service —</option>
                  {Object.keys(servicesByCategory).sort().map((cat) => {
                    const catComingSoon = cat === 'Trade Works & Administration';
                    return (
                      <optgroup key={cat} label={catComingSoon ? `${cat} (Coming Soon)` : cat}>
                        {servicesByCategory[cat].map((s) => {
                          const label = catComingSoon
                            ? `${s.name} — Coming Soon`
                            : `${s.name} — A$${s.deposit_amount.toFixed(0)} deposit`;
                          return (
                            <option key={s.id} value={s.id} disabled={catComingSoon} label={label}>
                              {label}
                            </option>
                          );
                        })}
                      </optgroup>
                    );
                  })}
                </select>
                {selectedService && (
                  <p
                    data-testid="booking-selected-service-blurb"
                    className="mt-2 text-xs text-slate-600"
                  >
                    {selectedService.description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Urgency</label>
                <select
                  data-testid={PRE_ASSESSMENT.urgencySelect}
                  value={form.urgency}
                  onChange={(e) => setForm({ ...form, urgency: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                >
                  {URGENCY_LEVELS.map((u) => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Property Address</label>
                <input
                  data-testid={PRE_ASSESSMENT.addressInput}
                  type="text"
                  value={form.property_address}
                  onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>

              {/* ============ SECTION A — All documents, pictures & photos ============ */}
              <section data-testid="intake-section-a" className="pt-4 border-t border-slate-200">
                <header className="flex items-start gap-3 mb-3">
                  <span className="w-9 h-9 bg-blue-600 text-white text-lg font-black rounded-sm flex items-center justify-center flex-shrink-0" style={{ fontFamily: 'Outfit' }}>
                    A
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-black" style={{ fontFamily: 'Outfit' }}>
                      <span className="text-blue-600">A</span>ll documents, pictures &amp; photos
                    </h3>
                    <p className="text-xs text-slate-600">
                      Upload everything that frames your matter — defect photos, plans, scope docs, quotes, contracts.
                    </p>
                  </div>
                </header>
                <input
                  type="file"
                  accept={USER_UPLOAD_ACCEPT_ATTR}
                  multiple
                  onChange={handleFileSelect}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">
                  JPEG or PDF only. Max {MAX_FILE_SIZE_LABEL} per file.
                </p>
                {pendingFiles.length > 0 && (
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {pendingFiles.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="relative aspect-square bg-slate-100 rounded-sm overflow-hidden">
                        {f.type === 'application/pdf' ? (
                          <div className="w-full h-full flex flex-col items-center justify-center p-2">
                            <FileText className="w-8 h-8 text-slate-500 mb-1" />
                            <span className="text-xs text-slate-700 text-center truncate max-w-full">{f.name}</span>
                          </div>
                        ) : (
                          <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                        )}
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="absolute top-1 right-1 bg-black bg-opacity-70 text-white p-1 rounded-sm hover:bg-opacity-90"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* ============ SECTION B — Building concerns, comments & help ============ */}
              <section data-testid="intake-section-b" className="pt-4 border-t border-slate-200">
                <header className="flex items-start gap-3 mb-3">
                  <span className="w-9 h-9 bg-blue-600 text-white text-lg font-black rounded-sm flex items-center justify-center flex-shrink-0" style={{ fontFamily: 'Outfit' }}>
                    B
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-black" style={{ fontFamily: 'Outfit' }}>
                      <span className="text-blue-600">B</span>uilding concerns, comments &amp; help required
                    </h3>
                    <p className="text-xs text-slate-600">
                      What worries you, where on the property, when it started — and what kind of help you're seeking. Plain English welcome.
                    </p>
                  </div>
                </header>
                <textarea
                  data-testid={PRE_ASSESSMENT.descriptionInput}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  rows="4"
                  placeholder="Describe the issue, location on property, when it started, and what kind of help, comments or advice you're seeking from All Build Consult."
                  required
                />
              </section>

              {/* ============ SECTION C — Conclusion, Solution or Further reports ============ */}
              <section data-testid="intake-section-c" className="pt-4 border-t border-slate-200">
                <header className="flex items-start gap-3 mb-3">
                  <span className="w-9 h-9 bg-blue-600 text-white text-lg font-black rounded-sm flex items-center justify-center flex-shrink-0" style={{ fontFamily: 'Outfit' }}>
                    C
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-black" style={{ fontFamily: 'Outfit' }}>
                      <span className="text-blue-600">C</span>onclusion, solution or further reports required
                    </h3>
                    <p className="text-xs text-slate-600">
                      The outcome you'd like — a structured report, rectification advice, follow-up specialist, or close-out path.
                    </p>
                  </div>
                </header>
                <textarea
                  data-testid={PRE_ASSESSMENT.outcomeInput}
                  value={form.expected_outcome}
                  onChange={(e) => setForm({ ...form, expected_outcome: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  rows="3"
                  placeholder="What do you hope to achieve? e.g. defect report, rectification advice, follow-up specialist referral, compliance certificate, close-out path…"
                  required
                />
              </section>

              <div>
                <label className="block text-sm font-medium text-black mb-2 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  Select an Available Slot (All Build Consult team to respond)
                </label>
                <p
                  data-testid="booking-slot-disclaimer"
                  className="text-xs text-slate-600 leading-relaxed mb-3 p-3 bg-blue-50 border-l-2 border-blue-600 rounded-sm"
                >
                  <strong className="text-blue-800">All Build Consult team to respond.</strong>{' '}
                  Upon initial review and confirmation we could help, before any deposit payment.
                  Once we have confirmed, we will request the deposit to be processed.
                  <br />
                  <strong className="text-blue-800">Disclaimer:</strong>{' '}
                  Please allow an initial review time from a minimum of 24 hours up to 3 days
                  response time for each report or request. The actual completed report or
                  discussion may range from 48 hours to 5 business days — all subject to each type
                  of report, request and project type.
                </p>

                {/* Lead-time block-out hint — visible on every render so the
                    user understands why the first business-week is greyed out. */}
                {earliestBookableDate && (
                  <p
                    data-testid="booking-lead-time-banner"
                    className="text-xs text-amber-900 leading-relaxed mb-3 p-3 bg-amber-50 border-l-2 border-amber-500 rounded-sm"
                  >
                    <strong>{leadBusinessDays}-business-day review window:</strong>{' '}
                    The next {leadBusinessDays} business days are blocked out so the All Build
                    Consult team can complete the initial review &amp; respond to your
                    pre-assessment. <strong>First available date</strong>:{' '}
                    <strong className="text-black">
                      {new Date(`${earliestBookableDate}T00:00:00+10:00`).toLocaleDateString(
                        'en-AU',
                        { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Australia/Sydney' }
                      )}
                    </strong>.
                  </p>
                )}
                <div className="grid-border bg-slate-50 p-4">
                  {/* Month navigator */}
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <button
                      type="button"
                      data-testid="booking-calendar-prev-month"
                      onClick={goPrevMonth}
                      disabled={!canPrev}
                      aria-label="Previous month"
                      className={`p-1.5 rounded-sm border transition-all ${canPrev ? 'border-slate-300 hover:bg-white' : 'border-slate-200 text-slate-300 cursor-not-allowed'}`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div
                      data-testid="booking-calendar-month-label"
                      className="text-sm font-semibold text-black"
                      style={{ fontFamily: 'Outfit' }}
                    >
                      {monthLabel}
                    </div>
                    <button
                      type="button"
                      data-testid="booking-calendar-next-month"
                      onClick={goNextMonth}
                      disabled={!canNext}
                      aria-label="Next month"
                      className={`p-1.5 rounded-sm border transition-all ${canNext ? 'border-slate-300 hover:bg-white' : 'border-slate-200 text-slate-300 cursor-not-allowed'}`}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Day-of-week headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {DAY_LABELS.map((d) => (
                      <div
                        key={d}
                        className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 text-center py-1"
                      >
                        {d}
                      </div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div
                    data-testid="booking-calendar-grid"
                    className="grid grid-cols-7 gap-1"
                  >
                    {monthGrid.map((cell, i) => {
                      const { selectable, reason } = dayState(cell);
                      const isSelected = cell.dateStr === selectedDate;
                      const isOtherMonth = !cell.inMonth;
                      return (
                        <button
                          key={`${cell.dateStr}-${i}`}
                          type="button"
                          data-testid={`booking-calendar-day-${cell.dateStr}`}
                          data-day-state={selectable ? 'available' : 'disabled'}
                          onClick={() => {
                            if (!selectable) return;
                            setSelectedDate(cell.dateStr);
                            setSelectedSlot('');
                          }}
                          disabled={!selectable}
                          title={selectable ? '' : reason}
                          className={`aspect-square text-sm rounded-sm border transition-all flex flex-col items-center justify-center gap-0.5 ${
                            isSelected && selectable
                              ? 'bg-black text-white border-black'
                              : selectable
                                ? `bg-white border-slate-300 hover:bg-slate-100 ${isOtherMonth ? 'opacity-60' : 'text-black'}`
                                : `border-slate-200 cursor-not-allowed ${isOtherMonth ? 'bg-slate-50 text-slate-300' : 'bg-slate-100 text-slate-400 line-through'}`
                          }`}
                        >
                          <span>{cell.day}</span>
                          {!selectable && !isOtherMonth && reason === 'Public holiday' && (
                            <span className="text-[8px] uppercase tracking-tight text-amber-600 leading-none">PH</span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="flex items-center gap-3 mt-3 text-[10px] text-slate-500 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block w-3 h-3 bg-white border border-slate-300 rounded-sm" />
                      Available
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block w-3 h-3 bg-black rounded-sm" />
                      Selected
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block w-3 h-3 bg-slate-100 border border-slate-200 rounded-sm" />
                      Unavailable
                    </span>
                    <span className="inline-flex items-center gap-1 text-amber-600">
                      PH = Public holiday
                    </span>
                  </div>

                  {/* Time slot dropdown — only shown when a date is selected */}
                  {selectedDate && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Choose a time on{' '}
                        <strong className="text-black">
                          {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-AU', {
                            weekday: 'long', month: 'long', day: 'numeric'
                          })}
                        </strong>
                        {' '}(AEST, 1-hour intervals — noon excluded)
                      </label>
                      <select
                        data-testid="booking-time-slot-select"
                        value={selectedSlot}
                        onChange={(e) => setSelectedSlot(e.target.value)}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-sm bg-white text-sm focus:ring-2 focus:ring-blue-600"
                      >
                        <option value="">— Select a time —</option>
                        {slotsForSelectedDate.map((s) => {
                          // Render time in AEST regardless of viewer's browser timezone.
                          const startLabel = new Date(s.slot).toLocaleTimeString('en-AU', {
                            hour: 'numeric', minute: '2-digit', hour12: true,
                            timeZone: AEST_TZ
                          });
                          const label = s.available
                            ? `${startLabel} AEST`
                            : `${startLabel} AEST (booked)`;
                          return (
                            <option
                              key={s.slot}
                              value={s.slot}
                              disabled={!s.available}
                              label={label}
                            >
                              {label}
                            </option>
                          );
                        })}
                      </select>
                      {selectedSlot && (
                        <p className="mt-2 text-sm text-slate-700">
                          Selected: <strong className="text-black">{formatSlot(selectedSlot)}</strong>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div data-testid="booking-deposit-display" className="grid-border bg-slate-50 p-4">
                <div className="flex items-baseline justify-between gap-4 mb-1">
                  <span className="text-sm font-medium text-black">Initial Deposit (AUD)</span>
                  <span className="text-2xl font-bold text-blue-600">
                    A${(selectedService?.deposit_amount ?? form.budget).toFixed(0)}
                  </span>
                </div>
                {selectedService && (
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span>
                      Full price: <span className="font-medium text-black">A${selectedService.full_price.toFixed(0)}</span>
                      {selectedService.full_price > 0 &&
                        Math.abs(selectedService.deposit_amount / selectedService.full_price - 0.5) < 0.01 && (
                          <span className="ml-2 text-slate-500">(deposit = 50% of total)</span>
                        )}
                    </span>
                    <span className="text-slate-500">From the Schedule of Reporting Services</span>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Initial deposit is processed before the All Build Consult team begins the assessment.
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800">
                  {error}
                </div>
              )}

              <div className="flex gap-4 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  data-testid={PRE_ASSESSMENT.submitButton}
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Pre-Assessment'}
                </button>
              </div>
            </form>
          </div>
        )}

        {bookings.length === 0 && !showForm ? (
          <div className="grid-border bg-white p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>No bookings yet</h3>
            <p className="text-slate-600 mb-6">Start with a pre-assessment intake to engage the All Build Consult team</p>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all"
            >
              Start Pre-Assessment
            </button>
          </div>
        ) : (
          <>
            {/* Status tabs: New / Current / Old */}
            <div
              data-testid="bookings-status-tabs"
              className="grid-border bg-white p-2 mb-5 inline-flex gap-1 flex-wrap"
            >
              {[
                { key: 'all', label: 'All', count: bookings.length },
                { key: 'new', label: 'New', count: bookingsByStatus.new.length },
                { key: 'current', label: 'Current', count: bookingsByStatus.current.length },
                { key: 'old', label: 'Old', count: bookingsByStatus.old.length }
              ].map((t) => (
                <button
                  key={t.key}
                  data-testid={`bookings-tab-${t.key}`}
                  onClick={() => setActiveTab(t.key)}
                  className={`px-4 py-2 rounded-sm text-sm font-medium transition-all flex items-center gap-2 ${
                    activeTab === t.key
                      ? 'bg-black text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {t.label}
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${
                      activeTab === t.key ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                    }`}
                  >
                    {t.count}
                  </span>
                </button>
              ))}
            </div>

            {filteredBookings.length === 0 ? (
              <div className="grid-border bg-white p-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-600">No {activeTab !== 'all' ? activeTab : ''} bookings in this view.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBookings.map((b) => {
                  const ageStatus = bookingAgeStatus(b);
                  const isPaid = b.payment_status === 'paid';
                  const isExternalConsultant = b.consultant_email !== 'team@allbuildconsult.com';
                  const isRated = ratedBookingIds.has(b.id);
                  return (
                    <div key={b.id} data-testid="booking-card" className="grid-border bg-white p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>
                              {b.service_type}
                            </h3>
                            <span
                              data-testid="booking-age-badge"
                              className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-sm ${AGE_STATUS_BADGE[ageStatus]}`}
                            >
                              {AGE_STATUS_LABEL[ageStatus]}
                            </span>
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-sm">
                              {STAGE_LABELS[b.stage] || b.stage}
                            </span>
                            {b.urgency === 'urgent' && (
                              <span className="px-3 py-1 bg-red-100 text-red-800 text-xs rounded-sm flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Urgent
                              </span>
                            )}
                          </div>
                      <p className="text-sm text-slate-600 mb-2">{b.description}</p>
                      <div className="text-sm text-slate-500 space-y-1">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4" />
                          <span>{b.preferred_slot ? formatSlot(b.preferred_slot) : b.preferred_date}</span>
                        </div>
                        {b.property_address && <div>{b.property_address}</div>}
                        <div>
                          Assigned to: <strong className="text-black">
                            {isExternalConsultant ? b.consultant_email : 'All Build Consult Team'}
                          </strong>
                        </div>
                      </div>
                      {b.report_text && (
                        <div className="mt-3 p-3 bg-slate-50 rounded-sm border-l-2 border-blue-600">
                          <p className="text-xs font-semibold text-blue-800 mb-1">TEAM REPORT</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{b.report_text}</p>
                        </div>
                      )}
                      {b.report_pdf_path && (
                        <a
                          href={`${API}/bookings/${b.id}/report-pdf?auth=${token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-black text-white rounded-sm hover:bg-slate-800 transition-all text-sm font-medium"
                        >
                          <Download className="w-4 h-4" />
                          Download Report (PDF)
                        </a>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                        A${b.budget.toFixed(2)}
                      </div>
                      <span className={`inline-block mt-2 px-3 py-1 text-xs rounded-sm ${
                        isPaid ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'
                      }`}>
                        {b.payment_status}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>Created {new Date(b.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex gap-2">
                      {!isPaid && (
                        <button
                          onClick={() => handlePayment(b.id)}
                          disabled={processingPayment[b.id]}
                          className="px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-all text-sm flex items-center gap-2 disabled:opacity-50"
                        >
                          <DollarSign className="w-4 h-4" />
                          {processingPayment[b.id] ? 'Processing...' : 'Pay Deposit'}
                        </button>
                      )}
                      {isPaid && isExternalConsultant && !isRated && (
                        <button
                          data-testid={RATING.rateButton}
                          onClick={() => setRatingBooking(b)}
                          className="px-4 py-2 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all text-sm"
                        >
                          Rate Consultant
                        </button>
                      )}
                      {isPaid && isExternalConsultant && isRated && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Reviewed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
              </div>
            )}
          </>
        )}
      </div>

      <RatingModal
        open={!!ratingBooking}
        booking={ratingBooking}
        onClose={() => setRatingBooking(null)}
        onSubmit={handleRatingSubmit}
      />
    </div>
  );
}
